import * as React from 'react';

import { DateTime, Duration } from 'luxon';

import { NavLinkButton } from '@shared/components/bootstrap';

import { Download as AudioDownload } from '@admin/components/audio/download';
import { Listen as AudioListen } from '@admin/components/audio/listen';
import { titleize } from '@admin/components/helpers/titleize';
import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { phonecallURL, editLeadURL, accountURL, dispatchURL } from '@admin/config/routes';

interface User {
  name?: string;
}

interface Conversation {
  playback_url?: string;
  user?: User;
}

interface Voicemail {
  playback_url?: string;
}

interface Lead {
  id?: number;
  account_id?: number;
}

interface PhoneCall {
  id: string;
  status: string;
  direction: string;
  phone?: string;
  system_phone?: string;
  start_at?: string;
  duration?: number;
  conversations: Conversation[];
  voicemails: Voicemail[];
  lead?: Lead;
  dispatch_id?: number;
}

/**
 * Formats duration in seconds to hh:mm:ss format.
 *
 * @param seconds - Duration in seconds.
 * @returns Formatted duration string or '--:--' if invalid.
 */
const formatDuration = (seconds?: number): string => {
  const number = Math.round(Number(seconds)) || 0;
  if (number <= 0) {
    return '--:--';
  }
  return Duration.fromObject({ seconds: number }).toFormat('hh:mm:ss');
};

/**
 * Formats a date string to MM/dd/yyyy format in the specified timezone.
 *
 * @param dateString - The ISO date string.
 * @param timezone - The timezone to use for formatting.
 * @returns Formatted date string.
 */
const formatDate = (dateString: string, timezone?: string): string => {
  const dt = timezone ? DateTime.fromISO(dateString).setZone(timezone) : DateTime.fromISO(dateString);
  return dt.toFormat('MM/dd/yyyy');
};

/**
 * Formats a date string to h:mm a z format in the specified timezone.
 *
 * @param dateString - The ISO date string.
 * @param timezone - The timezone to use for formatting.
 * @returns Formatted time string with timezone abbreviation.
 */
const formatTime = (dateString: string, timezone?: string): string => {
  const dt = timezone ? DateTime.fromISO(dateString).setZone(timezone) : DateTime.fromISO(dateString);
  return dt.toFormat('h:mm a ZZZZ');
};

/**
 * Determines if the agent name should be shown for a phone call.
 *
 * @param phoneCall - The phone call object.
 * @returns Whether to show the agent name.
 */
const showAgent = (phoneCall: PhoneCall): boolean =>
  phoneCall.direction === 'outbound' ||
  (phoneCall.status !== 'missed' && phoneCall.status !== 'hungup' && phoneCall.status !== 'voicemail');

/**
 * Gets the last conversation from a phone call.
 *
 * @param phoneCall - The phone call object.
 * @returns The last conversation or undefined.
 */
const getLastConversation = (phoneCall: PhoneCall): Conversation | undefined =>
  phoneCall.conversations[phoneCall.conversations.length - 1];

export const PhoneCallTable: React.FC<{
  calls?: PhoneCall[];
  timezone?: string;
}> = ({ calls, timezone }) => {
  if (!calls || calls.length === 0) {
    return (
      <table className="table table-striped" id="phone-calls-list">
        <thead>
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>Direction</th>
            <th>Agent</th>
            <th>Date</th>
            <th>Time</th>
            <th>Length</th>
            <th>Customer Phone</th>
            <th>Marketing Phone</th>
            <th>Listen</th>
            <th>Download</th>
            <th>Category</th>
          </tr>
        </thead>
        <tbody />
      </table>
    );
  }

  return (
    <table className="table table-striped" id="phone-calls-list">
      <thead>
        <tr>
          <th>ID</th>
          <th>Status</th>
          <th>Direction</th>
          <th>Agent</th>
          <th>Date</th>
          <th>Time</th>
          <th>Length</th>
          <th>Customer Phone</th>
          <th>Marketing Phone</th>
          <th>Listen</th>
          <th>Download</th>
          <th>Category</th>
        </tr>
      </thead>
      <tbody>
        {calls.map((phoneCall) => {
          const lastConversation = getLastConversation(phoneCall);
          const hasVoicemailWithNoConversations =
            phoneCall.voicemails[0]?.playback_url && phoneCall.conversations.length === 0;
          const playbackUrl =
            lastConversation?.playback_url ||
            (hasVoicemailWithNoConversations ? phoneCall.voicemails[0].playback_url : undefined);

          return (
            <tr key={phoneCall.id}>
              <td>
                <NavLinkButton kind="default" to={phonecallURL({ id: phoneCall.id })}>
                  {phoneCall.id || '-'}
                </NavLinkButton>
              </td>
              <td>
                <span className="label label-default">{titleize(phoneCall.status)}</span>
              </td>
              <td>{titleize(phoneCall.direction)}</td>
              <td>{showAgent(phoneCall) ? lastConversation?.user?.name || '-' : '-'}</td>
              <td>{phoneCall.start_at ? formatDate(phoneCall.start_at, timezone) : '-'}</td>
              <td>{phoneCall.start_at ? formatTime(phoneCall.start_at, timezone) : '-'}</td>
              <td>{formatDuration(phoneCall.duration)}</td>
              <td>{phoneCall.phone ? <PhoneFormatter number={phoneCall.phone} /> : '-'}</td>
              <td>{phoneCall.system_phone ? <PhoneFormatter number={phoneCall.system_phone} /> : '-'}</td>
              <td>
                {playbackUrl ? (
                  <AudioListen
                    src={playbackUrl}
                    pageID={phoneCall.id}
                    agent={lastConversation?.user?.name}
                    date={phoneCall.start_at}
                  />
                ) : (
                  '-'
                )}
              </td>
              <td>{playbackUrl ? <AudioDownload src={playbackUrl} /> : '-'}</td>
              <td>
                {phoneCall.lead?.id && !phoneCall.lead?.account_id && (
                  <NavLinkButton kind="default" to={editLeadURL({ id: phoneCall.lead.id })}>
                    Lead
                  </NavLinkButton>
                )}
                {phoneCall.lead?.account_id && (
                  <NavLinkButton kind="default" to={accountURL({ id: phoneCall.lead.account_id })}>
                    Account
                  </NavLinkButton>
                )}
                {phoneCall.dispatch_id && (
                  <NavLinkButton kind="default" to={dispatchURL({ id: phoneCall.dispatch_id })}>
                    Dispatch
                  </NavLinkButton>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
